import { api } from './api';

export interface Subscription {
  id?: number;
  userId: number;
  storeId: number;
  planType: string; // 'monthly' | 'yearly' — 현재 이용 중인 플랜
  /** 다음 결제일부터 적용될 플랜. 있으면 결제일 전까지는 planType이 현재, 결제 후 적용 시 planType으로 반영됨 */
  pendingPlanType?: string | null; // 'monthly' | 'yearly'
  status: string; // 'pending' | 'trial' | 'active' | 'paused' | 'payment_failed' | 'suspended' | 'cancelled' | 'expired'
  startDate?: string;
  endDate?: string;
  nextPaymentDate?: string;
  trialStartDate?: string;
  trialEndDate?: string;
  amount: number;
  /** 결제 유입. null/portone = 홈페이지·포트원, "external" = 크라우드펀딩 등 외부 플랫폼 */
  paymentSource?: string | null;
  /** 외부 전환 특별가 적용 여부. true면 연 69,000원 특별가 */
  isExternalConversionSpecial?: boolean;
  /** 프로모션 코드 적용 시 스냅샷 */
  promotionCodeId?: number | null;
  promotionCodeString?: string | null;
  discountAmount?: number | null;
  discountPercent?: number | null;
  promotionAppliedAt?: string | null;
  promotionBenefitEndDate?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

/** 프로모션 검증 응답 */
export interface PromoteValidateResponse {
  valid: boolean;
  errorCode?: string;
  message?: string;
  estimatedAmount?: number;
  benefitMonths?: number;
}

/** 프로모션 적용 응답 */
export interface PromoteApplyResponse {
  nextPaymentDate?: string;
  appliedAmount?: number;
  promotionCode?: string;
  message?: string;
}

/** 가입 경로: 본사면 구독 API 호출, 외부(와디즈 등)면 API 호출 없이 외부결제용 더미 반환 */
export const subscriptionApi = {
  // 사용자 구독 정보 조회.
  // registrationSource === '외부'일 때: skipApiForExternal !== false면 API 호출 없이 더미 반환.
  // skipApiForExternal === false면 실제 API 호출(이용 기간 만료 여부 확인용).
  getSubscriptionByUserId: async (
    userId: number,
    options?: { registrationSource?: string | null; skipApiForExternal?: boolean }
  ) => {
    const isExternal = options?.registrationSource === '외부';
    const skipForExternal = options?.skipApiForExternal !== false;
    if (isExternal && skipForExternal) {
      console.log('[SUBSCRIPTION API] 외부(와디즈) 경로 - 구독 API 호출 생략');
      const dummy: Subscription = {
        userId,
        storeId: 0,
        planType: 'monthly',
        status: 'active',
        amount: 0,
        paymentSource: 'external',
      };
      return { data: dummy };
    }
    console.log('[SUBSCRIPTION API] 사용자 구독 정보 조회:', userId);
    return await api.get<Subscription>(`/subscriptions/user/${userId}`);
  },

  // 구독 상세 조회
  getSubscription: async (id: number) => {
    console.log('[SUBSCRIPTION API] 구독 상세 조회:', id);
    return await api.get<Subscription>(`/subscriptions/${id}`);
  },

  // 구독 취소
  cancelSubscription: async (id: number) => {
    console.log('[SUBSCRIPTION API] 구독 취소:', id);
    return await api.put<Subscription>(`/subscriptions/${id}/cancel`, {});
  },

  // 플랜 변경
  changePlan: async (id: number, planType: string) => {
    console.log('[SUBSCRIPTION API] 플랜 변경:', id, planType);
    return await api.put<Subscription>(`/subscriptions/${id}/change-plan`, { planType });
  },

  // 구독 상태 업데이트 (재결제 시 사용)
  updateSubscriptionStatus: async (id: number, status: string) => {
    console.log('[SUBSCRIPTION API] 구독 상태 업데이트:', id, status);
    return await api.put<Subscription>(`/subscriptions/${id}/reactivate`, { status });
  },

  // 관리자용: 모든 구독 목록 조회
  getAllSubscriptions: async (status?: string) => {
    console.log('[SUBSCRIPTION API] 모든 구독 목록 조회:', status);
    const url = status ? `/subscriptions?status=${status}` : '/subscriptions';
    return await api.get<Subscription[]>(url);
  },

  /** 관리자용: 구독 시작일/종료일 변경. 변경 후 목록/상세에 반영하려면 호출 측에서 상태 갱신. */
  updateSubscriptionDates: async (id: number, startDate?: string | null, endDate?: string | null) => {
    const body: { startDate?: string | null; endDate?: string | null } = {};
    if (startDate != null) body.startDate = startDate;
    if (endDate != null) body.endDate = endDate;
    await api.patch(`/subscriptions/${id}/dates`, body);
  },

  /** 외부결제 기간 만료 후 본사(포트원)로 전환. 결제 처리 + 구독/Store 업데이트. */
  convertExternalToPortone: async (planType: 'monthly' | 'yearly') => {
    return await api.post<Subscription>('/subscriptions/convert-external-to-portone', { planType });
  },

  /** 프로모션 코드 검증 (유효성·에러 사유 반환) */
  validatePromotion: async (code: string) => {
    const res = await api.get<PromoteValidateResponse>('/promotions/validate', { params: { code: code?.trim() || '' } });
    return res;
  },

  /** 프로모션 코드 적용 (구독에 할인 스냅샷 저장) */
  applyPromotion: async (code: string) => {
    const res = await api.post<PromoteApplyResponse>('/promotions/apply', { code: code?.trim() || '' });
    return res;
  },
};
